import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";

import {
  CHAPTER_ORDER,
  CHAPTER_LABELS_FR,
  CHAPTER_PURPOSE_FR,
  CHAPTER_FIELDS,
  emptyChapter,
  migrateChapter,
  COMMISSION_CHAPTER_ORDER,
  COMMISSION_CHAPTER_LABELS,
  COMMISSION_STATUSES,
  COMMISSION_STATUS_LABELS,
  emptyCommissionChapter,
} from "@/data/projectChapters";

// -----------------------------------------------------------------------------
// Maison Dodji — Project Space (private founder interface).
//
// Reached at /atelier. Passphrase-gated. Rendered outside the public Layout.
// Language: French only (the Maison always speaks in French internally).
// -----------------------------------------------------------------------------

const SESSION_KEY = "md-atelier-session";

const FIELD_LABELS_FR = {
  body: "Introduction", fullName: "Nom", city: "Ville", contact: "Contact",
  background: "Parcours", address: "Adresse", style: "Style", era: "Époque",
  rooms: "Pièces principales", atmosphere: "Atmosphère",
  volumes: "Volumes", materials: "Matériaux", light: "Lumière",
  ambiance: "Ambiance",
  summary: "Résumé", brand: "Marque", reference: "Référence",
  notes: "Notes", intent: "Intention", horizon: "Horizon",
  hasSafe: "Coffre existant", model: "Modèle",
  capacity: "Capacité", location: "Emplacement",
  limitations: "Limitations",
  rhythm: "Rythme de vie", travel: "Voyages", interests: "Centres d'intérêt",
  imageUrl: "Image (URL)", caption: "Légende",
  name: "Nom", agency: "Agence",
  label: "Zone", dimensions: "Dimensions",
  access: "Accès", electrical: "Électricité",
  humidity: "Hygrométrie", security: "Sécurité", other: "Autres",
  prompt: "Question", nextSteps: "Prochaines étapes",
  yes: "Oui", no: "Non", na: "Sans objet",
};

const api = async (path, opts = {}) => {
  const session = localStorage.getItem(SESSION_KEY);
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (session) headers.Authorization = `Bearer ${session}`;
  const res = await fetch(path, { ...opts, headers });
  if (res.status === 401) {
    localStorage.removeItem(SESSION_KEY);
    throw new Error("unauthorized");
  }
  return res;
};

export default function Atelier() {
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY));
  const [spaces, setSpaces] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Project Space — Maison Dodji";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setBusy(true);
      const r = await api("/api/atelier/spaces");
      if (!r.ok) throw new Error("load_failed");
      const data = await r.json();
      setSpaces(data.spaces || []);
    } catch (e) {
      if (e.message === "unauthorized") setSession(null);
      setError(e.message);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (session) refresh(); }, [session, refresh]);

  if (!session) return <Gate onLogin={(s) => { setSession(s); localStorage.setItem(SESSION_KEY, s); }} />;

  if (current) return (
    <SpaceEditor
      token={current}
      onClose={() => { setCurrent(null); refresh(); }}
    />
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))] py-16 px-6 md:px-12">
      <div className="max-w-[960px] mx-auto">
        <div className="flex justify-between items-baseline mb-10">
          <div>
            <p className="font-eyebrow text-[hsl(var(--md-stone))]">Maison Dodji</p>
            <h1 className="font-display text-[2rem] mt-2">Project Spaces en cours</h1>
          </div>
          <div className="flex gap-6 font-eyebrow">
            <Link to="/" className="text-[hsl(var(--md-link))]" data-testid="atelier-return-site">Retour au site</Link>
            <button
              onClick={() => { localStorage.removeItem(SESSION_KEY); setSession(null); }}
              className="text-[hsl(var(--md-link))]"
              data-testid="atelier-signout"
            >Se retirer</button>
          </div>
        </div>

        <NewSpaceForm onCreated={(t) => setCurrent(t)} />

        {busy && <p className="font-body text-[hsl(var(--md-stone))] mt-8">Ouverture…</p>}
        {error && <p className="font-body text-red-800 mt-4">{error}</p>}

        <div className="mt-14">
          {spaces.length === 0 && !busy && (
            <p className="font-body text-[hsl(var(--md-stone))]">Aucun Project Space n'est ouvert pour l'instant.</p>
          )}
          <ul className="divide-y divide-[hsl(var(--md-line))]">
            {spaces.map((s) => (
              <li key={s.token} className="py-5 flex items-baseline justify-between" data-testid={`atelier-space-${s.token}`}>
                <div>
                  <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-1">
                    {s.kind === "commission" ? "Private Commission" : "Étude privée"}
                  </p>
                  <p className="font-display text-[1.15rem]">{s.projectTitle}</p>
                  <p className="font-body text-[14px] text-[hsl(var(--md-stone))]">
                    {s.namedParty} · {s.language.toUpperCase()} · ouvert le {s.openedOn}
                    {s.kind === "commission" && s.commissionSlug && <span> · <code>{s.commissionSlug}</code></span>}
                    {s.kind === "commission" && s.publiclyPublished && <span> · <em>publié</em></span>}
                    {s.sealed && <span> · <em>scellé le {s.sealed.date}</em></span>}
                  </p>
                </div>
                <button
                  onClick={() => setCurrent(s.token)}
                  className="font-eyebrow text-[hsl(var(--md-link))] hover:text-[hsl(var(--md-ink))]"
                  data-testid={`atelier-open-${s.token}`}
                >
                  Ouvrir
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Gate({ onLogin }) {
  const [passphrase, setPassphrase] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const res = await fetch("/api/atelier/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase }),
      });
      if (!res.ok) throw new Error("no");
      const data = await res.json();
      onLogin(data.session);
    } catch { setErr("Passe non reconnue."); }
  };
  return (
    <div className="min-h-screen bg-[hsl(var(--md-chalk))] flex items-center justify-center px-6">
      <form onSubmit={submit} className="max-w-[420px] w-full" data-testid="atelier-gate">
        <p className="font-eyebrow text-[hsl(var(--md-stone))]">Maison Dodji</p>
        <h1 className="font-display text-[2rem] mt-3 mb-8 text-[hsl(var(--md-ink))]">Entrer</h1>
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder="Passe de la Maison"
          className="input-field"
          data-testid="atelier-passphrase"
          autoFocus
        />
        {err && <p className="font-body text-[14px] text-red-800 mt-3">{err}</p>}
        <button type="submit"
          className="mt-8 px-6 py-3 border border-[hsl(var(--md-ink))] font-eyebrow text-[hsl(var(--md-ink))]"
          data-testid="atelier-enter">
          Entrer
        </button>
      </form>
    </div>
  );
}

function NewSpaceForm({ onCreated }) {
  const [form, setForm] = useState({
    kind: "project",
    namedParty: "", projectTitle: "", language: "fr",
    openedOn: new Date().toISOString().slice(0, 10),
    pieceName: "", pieceNumber: "",
    commissionSlug: "",
  });
  const [busy, setBusy] = useState(false);
  const isCommission = form.kind === "commission";
  const create = async (e) => {
    e.preventDefault();
    if (!form.namedParty.trim() || !form.projectTitle.trim()) return;
    if (isCommission && !form.commissionSlug.trim()) return;
    setBusy(true);
    try {
      const r = await api("/api/atelier/spaces", { method: "POST", body: JSON.stringify(form) });
      const d = await r.json();
      onCreated(d.token);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={create} className="border border-[hsl(var(--md-line))] p-6 space-y-4" data-testid="atelier-new-space">
      <p className="font-eyebrow text-[hsl(var(--md-stone))]">
        {isCommission ? "Ouvrir une nouvelle Private Commission" : "Ouvrir un nouveau Project Space"}
      </p>
      <div>
        <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-2">Nature</label>
        <div className="flex gap-6 font-body text-[14px] text-[hsl(var(--md-ink))]">
          <label className="flex items-center gap-2">
            <input type="radio" name="kind" value="project" checked={!isCommission}
              onChange={() => setForm({ ...form, kind: "project" })}
              data-testid="atelier-kind-project" />
            Étude privée
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="kind" value="commission" checked={isCommission}
              onChange={() => setForm({ ...form, kind: "commission" })}
              data-testid="atelier-kind-commission" />
            Private Commission
          </label>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <input required placeholder="Commanditaire (nom)" value={form.namedParty}
          onChange={(e) => setForm({ ...form, namedParty: e.target.value })} className="input-field" data-testid="atelier-named-party" />
        <input required placeholder="Titre du projet" value={form.projectTitle}
          onChange={(e) => setForm({ ...form, projectTitle: e.target.value })} className="input-field" data-testid="atelier-project-title" />
        <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
          className="input-field" data-testid="atelier-language">
          <option value="fr">Français</option>
          <option value="en">English</option>
        </select>
        <input type="date" value={form.openedOn}
          onChange={(e) => setForm({ ...form, openedOn: e.target.value })} className="input-field" data-testid="atelier-opened-on" />
        <input placeholder="Pièce (optionnel)" value={form.pieceName}
          onChange={(e) => setForm({ ...form, pieceName: e.target.value })} className="input-field" />
        <input placeholder="Numéro de pièce (optionnel)" value={form.pieceNumber}
          onChange={(e) => setForm({ ...form, pieceNumber: e.target.value })} className="input-field" />
        {isCommission && (
          <input required placeholder="Slug public (ex : fred-courtot)" value={form.commissionSlug}
            onChange={(e) => setForm({ ...form, commissionSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") })}
            className="input-field md:col-span-2" data-testid="atelier-commission-slug" />
        )}
      </div>
      <button type="submit" disabled={busy}
        className="px-6 py-3 border border-[hsl(var(--md-ink))] font-eyebrow text-[hsl(var(--md-ink))]"
        data-testid="atelier-create">
        {busy ? "Ouverture…" : (isCommission ? "Ouvrir la Private Commission" : "Ouvrir le Project Space")}
      </button>
    </form>
  );
}

function SpaceEditor({ token, onClose }) {
  const [space, setSpace] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const r = await api(`/api/atelier/spaces/${token}`);
    if (r.ok) {
      const doc = await r.json();
      doc.chapters = (doc.chapters || []).map(migrateChapter);
      setSpace(doc);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const r = await api(`/api/atelier/spaces/${token}`, { method: "PUT", body: JSON.stringify(patch) });
    if (r.ok) {
      const doc = await r.json();
      doc.chapters = (doc.chapters || []).map(migrateChapter);
      setSpace(doc);
    } else {
      setErr("Enregistrement impossible.");
    }
  };

  const addChapter = (type) => {
    const next = [...(space.chapters || []), emptyChapter(type)];
    save({ chapters: next });
  };

  const updateChapter = (i, patch) => {
    const next = space.chapters.map((c, idx) => idx === i ? { ...c, ...patch } : c);
    save({ chapters: next });
  };

  const removeChapter = (i) => {
    if (!confirm("Retirer ce chapitre ?")) return;
    save({ chapters: space.chapters.filter((_, idx) => idx !== i) });
  };

  const seal = async () => {
    if (!confirm("Sceller définitivement le Project Space ? Cette action est irréversible.")) return;
    const r = await api(`/api/atelier/spaces/${token}/seal`, { method: "POST", body: JSON.stringify({}) });
    if (r.ok) {
      const doc = await r.json();
      doc.chapters = (doc.chapters || []).map(migrateChapter);
      setSpace(doc);
    }
  };

  if (!space) return <p className="p-8">Ouverture…</p>;

  if (space.kind === "commission") {
    return (
      <CommissionEditor
        space={space}
        onSave={save}
        onBack={onClose}
      />
    );
  }

  const publicUrl = `${window.location.origin}/projet/${token}`;
  const briefUrl = `${window.location.origin}/projet/${token}/brief`;
  const usedTypes = new Set((space.chapters || []).map((c) => c.type));
  const availableTypes = CHAPTER_ORDER.filter((t) => !usedTypes.has(t));

  return (
    <div className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))] py-12 px-6 md:px-12">
      <div className="max-w-[960px] mx-auto">
        <button onClick={onClose} className="font-eyebrow text-[hsl(var(--md-link))] mb-8" data-testid="editor-back">
          ← Retour aux Project Spaces
        </button>

        <div className="flex justify-between items-baseline mb-2">
          <p className="font-eyebrow text-[hsl(var(--md-stone))]">{space.projectTitle}</p>
          {space.sealed && (
            <span className="font-eyebrow text-[hsl(var(--md-ink))] bg-[hsl(var(--md-sand))] px-3 py-1 text-[11px]">
              Project Space clos · {space.sealed.date}
            </span>
          )}
        </div>
        <h1 className="font-display text-[1.85rem] text-[hsl(var(--md-ink))]">{space.namedParty}</h1>
        <p className="font-body text-[14px] text-[hsl(var(--md-stone))] mb-8">
          {space.language.toUpperCase()} · ouvert le {space.openedOn}
          {space.pieceName && <> · {space.pieceName}</>}
          {space.pieceNumber && <> · {space.pieceNumber}</>}
        </p>

        {/* Private URLs */}
        <div className="border border-[hsl(var(--md-line))] p-4 mb-10 bg-[hsl(var(--md-bone))] space-y-3">
          <div>
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">Lien confié au commanditaire</p>
            <div className="flex items-center gap-4">
              <code className="font-body text-[13px] text-[hsl(var(--md-ink))] break-all">{publicUrl}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
                className="font-eyebrow text-[hsl(var(--md-link))] shrink-0"
                data-testid="editor-copy-url"
              >
                {copied ? "Copié" : "Copier"}
              </button>
              <a href={publicUrl} target="_blank" rel="noreferrer"
                className="font-eyebrow text-[hsl(var(--md-link))] shrink-0"
                data-testid="editor-preview">Voir</a>
            </div>
          </div>
          <div className="pt-3 border-t border-[hsl(var(--md-line))]">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">Brief architectural</p>
            <a href={briefUrl} target="_blank" rel="noreferrer"
              className="font-eyebrow text-[hsl(var(--md-link))]"
              data-testid="editor-open-brief">Consulter le Brief architectural</a>
          </div>
        </div>

        {/* Chapters */}
        <div className="space-y-8">
          {(space.chapters || []).map((c, i) => (
            <ChapterCard
              key={i}
              chap={c}
              index={i}
              sealed={!!space.sealed}
              onChange={(patch) => updateChapter(i, patch)}
              onRemove={() => removeChapter(i)}
            />
          ))}
        </div>

        {!space.sealed && (
          <div className="mt-10 space-y-4">
            {availableTypes.length > 0 && (
              <div className="border border-[hsl(var(--md-line))] p-5">
                <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-3">Ajouter un chapitre</p>
                <div className="flex flex-wrap gap-2" data-testid="editor-chapter-picker">
                  {availableTypes.map((t) => (
                    <button
                      key={t}
                      onClick={() => addChapter(t)}
                      className="px-3 py-2 border border-[hsl(var(--md-line-strong))] font-body text-[13px] text-[hsl(var(--md-ink))] hover:bg-[hsl(var(--md-bone))]"
                      data-testid={`editor-add-${t}`}
                    >
                      {CHAPTER_LABELS_FR[t]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button onClick={seal}
              className="px-6 py-3 border border-[hsl(var(--md-ink))] bg-[hsl(var(--md-ink))] text-[hsl(var(--md-chalk))] font-eyebrow"
              data-testid="editor-seal">
              Sceller le Project Space
            </button>
          </div>
        )}

        {err && <p className="font-body text-red-800 mt-4">{err}</p>}
      </div>
    </div>
  );
}

function ChapterCard({ chap, index, sealed, onChange, onRemove }) {
  const spec = CHAPTER_FIELDS[chap.type] || [];
  const label = FIELD_LABELS_FR;

  const setField = (key, value) => {
    onChange({ fields: { ...chap.fields, [key]: value } });
  };
  const setListItem = (key, i, patch) => {
    const list = chap.fields[key] || [];
    const next = list.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    setField(key, next);
  };
  const addListItem = (key, itemFields) => {
    const empty = {}; itemFields.forEach((f) => { empty[f.key] = ""; });
    setField(key, [...(chap.fields[key] || []), empty]);
  };
  const removeListItem = (key, i) => {
    setField(key, (chap.fields[key] || []).filter((_, idx) => idx !== i));
  };

  return (
    <div className="border border-[hsl(var(--md-line))] p-6" data-testid={`editor-chapter-${chap.type}`}>
      <div className="flex justify-between items-baseline mb-2">
        <div>
          <p className="font-eyebrow text-[hsl(var(--md-stone))]">Chapitre {index + 1}</p>
          <h2 className="font-display text-[1.4rem] text-[hsl(var(--md-ink))] mt-1">{CHAPTER_LABELS_FR[chap.type]}</h2>
        </div>
        <div className="flex gap-4 items-center">
          <select
            value={chap.status || "draft"}
            onChange={(e) => onChange({ status: e.target.value })}
            disabled={sealed}
            className="font-eyebrow bg-transparent border-b border-[hsl(var(--md-line))] text-[hsl(var(--md-ink))]"
            data-testid={`editor-chapter-${chap.type}-status`}
          >
            <option value="draft">Brouillon</option>
            <option value="complete">Chapitre abouti</option>
          </select>
          <label className="font-body text-[13px] text-[hsl(var(--md-stone))] flex items-center gap-2">
            <input type="checkbox" checked={!!chap.shared} disabled={sealed}
              onChange={(e) => onChange({ shared: e.target.checked })}
              data-testid={`editor-chapter-${chap.type}-shared`} />
            Confié au client
          </label>
          {!sealed && (
            <button onClick={onRemove} className="font-eyebrow text-[hsl(var(--md-stone))]">Retirer</button>
          )}
        </div>
      </div>

      <p className="font-body italic text-[14px] text-[hsl(var(--md-stone))] mb-5">
        {CHAPTER_PURPOSE_FR[chap.type]}
      </p>

      <div className="space-y-4">
        {spec.map((f) => (
          <FieldRow
            key={f.key}
            f={f}
            value={chap.fields?.[f.key]}
            label={label[f.labelKey]}
            sealed={sealed}
            onSet={(v) => setField(f.key, v)}
            onSetItem={(i, patch) => setListItem(f.key, i, patch)}
            onAddItem={() => addListItem(f.key, f.itemFields)}
            onRemoveItem={(i) => removeListItem(f.key, i)}
          />
        ))}
      </div>

      <div className="mt-6 pt-5 border-t border-[hsl(var(--md-line))]">
        <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-2">
          Observations de la Maison
        </label>
        <textarea
          value={chap.observations || ""}
          onChange={(e) => onChange({ observations: e.target.value })}
          disabled={sealed}
          rows={3}
          placeholder="La Maison note ici, à la première personne, ce qu'elle a compris ou souhaite préciser."
          className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-2 font-body italic text-[15px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
          data-testid={`editor-chapter-${chap.type}-observations`}
        />
      </div>
    </div>
  );
}

function FieldRow({ f, value, label, sealed, onSet, onSetItem, onAddItem, onRemoveItem }) {
  if (f.kind === "text") {
    return (
      <div>
        <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">{label}</label>
        <input
          type="text"
          value={value || ""}
          disabled={sealed}
          onChange={(e) => onSet(e.target.value)}
          className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-2 font-body text-[15px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
          data-testid={`field-${f.key}`}
        />
      </div>
    );
  }
  if (f.kind === "textarea") {
    return (
      <div>
        <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">{label}</label>
        <textarea
          value={value || ""}
          disabled={sealed}
          onChange={(e) => onSet(e.target.value)}
          rows={4}
          className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-2 font-body text-[15px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
          data-testid={`field-${f.key}`}
        />
      </div>
    );
  }
  if (f.kind === "select") {
    return (
      <div>
        <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">{label}</label>
        <select
          value={value || ""}
          disabled={sealed}
          onChange={(e) => onSet(e.target.value)}
          className="bg-transparent border-b border-[hsl(var(--md-line-strong))] py-2 font-body text-[15px] text-[hsl(var(--md-ink))]"
          data-testid={`field-${f.key}`}
        >
          <option value="">—</option>
          {f.options.map((o) => (
            <option key={o.v} value={o.v}>{FIELD_LABELS_FR[o.labelKey]}</option>
          ))}
        </select>
      </div>
    );
  }
  if (f.kind === "list") {
    const list = Array.isArray(value) ? value : [];
    return (
      <div>
        <div className="flex justify-between items-baseline mb-2">
          <label className="font-eyebrow text-[hsl(var(--md-stone))]">{label}</label>
          {!sealed && (
            <button
              type="button"
              onClick={onAddItem}
              className="font-eyebrow text-[hsl(var(--md-link))]"
              data-testid={`field-${f.key}-add`}
            >
              + Ajouter
            </button>
          )}
        </div>
        <ul className="space-y-3">
          {list.map((item, i) => (
            <li key={i} className="border border-[hsl(var(--md-line))] p-3 grid md:grid-cols-2 gap-3" data-testid={`field-${f.key}-item-${i}`}>
              {f.itemFields.map((sub) => (
                <div key={sub.key} className={sub.kind === "textarea" ? "md:col-span-2" : ""}>
                  <label className="font-body text-[12px] text-[hsl(var(--md-stone))] block mb-1">
                    {FIELD_LABELS_FR[sub.labelKey]}
                  </label>
                  {sub.kind === "textarea" ? (
                    <textarea
                      value={item[sub.key] || ""}
                      disabled={sealed}
                      onChange={(e) => onSetItem(i, { [sub.key]: e.target.value })}
                      rows={2}
                      className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-1 font-body text-[14px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
                    />
                  ) : (
                    <input
                      type="text"
                      value={item[sub.key] || ""}
                      disabled={sealed}
                      onChange={(e) => onSetItem(i, { [sub.key]: e.target.value })}
                      className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-1 font-body text-[14px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
                    />
                  )}
                </div>
              ))}
              {!sealed && (
                <div className="md:col-span-2 flex justify-end">
                  <button type="button" onClick={() => onRemoveItem(i)}
                    className="font-eyebrow text-[hsl(var(--md-stone))]">Retirer</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

// -----------------------------------------------------------------------------
// CommissionEditor — Private Commission (Maison Dodji Paris, editorial).
//
// Separate schema from the 17-chapter Project Space. Owns:
//   - status (9 restrained states)
//   - hero image URL + alt (OG preview)
//   - meta description
//   - "Publier publiquement" toggle (OFF by default, immediately reversible)
//   - commissionChapters (9 editorial chapters, published flag per chapter)
// -----------------------------------------------------------------------------

function CommissionEditor({ space, onSave, onBack }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = space.commissionSlug
    ? `${window.location.origin}/private-commissions/${space.commissionSlug}`
    : "";
  const chapters = space.commissionChapters || [];
  const usedTypes = new Set(chapters.map((c) => c.type));
  const availableTypes = COMMISSION_CHAPTER_ORDER.filter((t) => !usedTypes.has(t));

  const setField = (patch) => onSave(patch);

  const addChapter = (type) => {
    setField({ commissionChapters: [...chapters, emptyCommissionChapter(type)] });
  };

  const updateChapter = (i, patch) => {
    setField({ commissionChapters: chapters.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  };

  const removeChapter = (i) => {
    if (!confirm("Retirer ce chapitre ?")) return;
    setField({ commissionChapters: chapters.filter((_, idx) => idx !== i) });
  };

  const togglePublish = () => {
    if (!space.publiclyPublished) {
      if (!confirm("Publier cette Private Commission publiquement ? Cette action est immédiatement réversible.")) return;
    }
    setField({ publiclyPublished: !space.publiclyPublished });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))] py-12 px-6 md:px-12">
      <div className="max-w-[960px] mx-auto">
        <button onClick={onBack} className="font-eyebrow text-[hsl(var(--md-link))] mb-8" data-testid="editor-back">
          ← Retour aux Project Spaces
        </button>

        <div className="flex justify-between items-baseline mb-2">
          <p className="font-eyebrow text-[hsl(var(--md-stone))]">Private Commission · {space.projectTitle}</p>
          <span
            className={
              "font-eyebrow px-3 py-1 text-[11px] " +
              (space.publiclyPublished
                ? "bg-[hsl(var(--md-ink))] text-[hsl(var(--md-chalk))]"
                : "bg-[hsl(var(--md-sand))] text-[hsl(var(--md-ink))]")
            }
            data-testid="commission-publish-state"
          >
            {space.publiclyPublished ? "Publié publiquement" : "Non publié"}
          </span>
        </div>
        <h1 className="font-display text-[1.85rem] text-[hsl(var(--md-ink))]">{space.namedParty}</h1>
        <p className="font-body text-[14px] text-[hsl(var(--md-stone))] mb-8">
          {space.language.toUpperCase()} · ouvert le {space.openedOn}
          {space.pieceName && <> · {space.pieceName}</>}
          {space.pieceNumber && <> · {space.pieceNumber}</>}
        </p>

        {/* Public URL + publication toggle */}
        <div className="border border-[hsl(var(--md-line))] p-4 mb-10 bg-[hsl(var(--md-bone))] space-y-4">
          <div>
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">Lien confié au commanditaire</p>
            <p className="font-body italic text-[13px] text-[hsl(var(--md-stone))]">
              Une Private Commission n'a pas de lien privé de type Project Space. Le lien ci-dessous est l'URL publique éditoriale.
            </p>
          </div>
          <div className="pt-3 border-t border-[hsl(var(--md-line))]">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">URL publique</p>
            <div className="flex items-center gap-4">
              <code className="font-body text-[13px] text-[hsl(var(--md-ink))] break-all">
                {publicUrl || "— (renseigner le slug)"}
              </code>
              {publicUrl && (
                <>
                  <button
                    onClick={() => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
                    className="font-eyebrow text-[hsl(var(--md-link))] shrink-0"
                    data-testid="commission-copy-url"
                  >
                    {copied ? "Copié" : "Copier"}
                  </button>
                  <a href={publicUrl} target="_blank" rel="noreferrer"
                    className="font-eyebrow text-[hsl(var(--md-link))] shrink-0"
                    data-testid="commission-preview">Voir</a>
                </>
              )}
            </div>
            <p className="font-body italic text-[13px] text-[hsl(var(--md-stone))] mt-2">
              L'accès à cette page requiert que la Maison ait activé « Publier publiquement ». Aucun index, aucun sitemap, aucune navigation ne renvoie vers cette URL.
            </p>
          </div>
          <div className="pt-3 border-t border-[hsl(var(--md-line))]">
            <label className="flex items-center gap-3 font-body text-[15px] text-[hsl(var(--md-ink))]">
              <input
                type="checkbox"
                checked={!!space.publiclyPublished}
                onChange={togglePublish}
                data-testid="commission-publish-toggle"
              />
              Publier publiquement
            </label>
            <p className="font-body italic text-[13px] text-[hsl(var(--md-stone))] mt-2">
              Immédiatement réversible. Retirer la publication rend la page publique aussitôt indisponible.
            </p>
          </div>
        </div>

        {/* Commission metadata */}
        <div className="border border-[hsl(var(--md-line))] p-6 mb-10 space-y-5" data-testid="commission-meta">
          <p className="font-eyebrow text-[hsl(var(--md-stone))]">Éléments de la commande</p>

          <div>
            <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Slug public</label>
            <input
              type="text" value={space.commissionSlug || ""}
              onChange={(e) => setField({ commissionSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") })}
              className="input-field"
              placeholder="ex : fred-courtot"
              data-testid="commission-slug"
            />
          </div>

          <div>
            <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">État du projet</label>
            <select
              value={space.status || ""}
              onChange={(e) => setField({ status: e.target.value })}
              className="input-field"
              data-testid="commission-status"
            >
              <option value="">—</option>
              {COMMISSION_STATUSES.map((s) => (
                <option key={s} value={s}>{COMMISSION_STATUS_LABELS.fr[s]}</option>
              ))}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Image d'ouverture (URL)</label>
              <input
                type="text" value={space.heroImage || ""}
                onChange={(e) => setField({ heroImage: e.target.value })}
                className="input-field"
                placeholder="Laisser vide tant qu'aucune image n'est approuvée"
                data-testid="commission-hero-image"
              />
            </div>
            <div>
              <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Légende de l'image (alt)</label>
              <input
                type="text" value={space.heroAlt || ""}
                onChange={(e) => setField({ heroAlt: e.target.value })}
                className="input-field"
                data-testid="commission-hero-alt"
              />
            </div>
          </div>

          <div>
            <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Introduction éditoriale</label>
            <textarea
              rows={5}
              value={space.metaDescription || ""}
              onChange={(e) => setField({ metaDescription: e.target.value })}
              className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-2 font-body text-[15px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
              placeholder="Texte introductif visible en tête de la page publique et utilisé pour les prévisualisations de partage."
              data-testid="commission-meta-description"
            />
          </div>
        </div>

        {/* Chapters */}
        <div className="space-y-8">
          {chapters.map((c, i) => (
            <CommissionChapterCard
              key={i}
              chap={c}
              index={i}
              onChange={(patch) => updateChapter(i, patch)}
              onRemove={() => removeChapter(i)}
            />
          ))}
        </div>

        {availableTypes.length > 0 && (
          <div className="mt-10 border border-[hsl(var(--md-line))] p-5">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-3">Ajouter un chapitre</p>
            <div className="flex flex-wrap gap-2" data-testid="commission-chapter-picker">
              {availableTypes.map((t) => (
                <button
                  key={t}
                  onClick={() => addChapter(t)}
                  className="px-3 py-2 border border-[hsl(var(--md-line-strong))] font-body text-[13px] text-[hsl(var(--md-ink))] hover:bg-[hsl(var(--md-bone))]"
                  data-testid={`commission-add-${t}`}
                >
                  {COMMISSION_CHAPTER_LABELS.fr[t]}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CommissionChapterCard({ chap, index, onChange, onRemove }) {
  const setImage = (i, patch) => {
    onChange({
      images: (chap.images || []).map((im, idx) => idx === i ? { ...im, ...patch } : im),
    });
  };
  const addImage = () => onChange({ images: [...(chap.images || []), { url: "", caption: "" }] });
  const removeImage = (i) => onChange({ images: (chap.images || []).filter((_, idx) => idx !== i) });

  return (
    <div className="border border-[hsl(var(--md-line))] p-6" data-testid={`commission-chapter-${chap.type}`}>
      <div className="flex justify-between items-baseline mb-4">
        <div>
          <p className="font-eyebrow text-[hsl(var(--md-stone))]">Chapitre {index + 1}</p>
          <h2 className="font-display text-[1.4rem] text-[hsl(var(--md-ink))] mt-1">
            {COMMISSION_CHAPTER_LABELS.fr[chap.type]}
          </h2>
        </div>
        <div className="flex gap-4 items-center">
          <label className="font-body text-[13px] text-[hsl(var(--md-stone))] flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!chap.published}
              onChange={(e) => onChange({ published: e.target.checked })}
              data-testid={`commission-chapter-${chap.type}-published`}
            />
            Publié publiquement
          </label>
          <button onClick={onRemove} className="font-eyebrow text-[hsl(var(--md-stone))]">Retirer</button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Date</label>
          <input
            type="date"
            value={chap.date || ""}
            onChange={(e) => onChange({ date: e.target.value })}
            className="input-field"
          />
        </div>
        <div>
          <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Texte éditorial</label>
          <textarea
            rows={5}
            value={chap.body || ""}
            onChange={(e) => onChange({ body: e.target.value })}
            className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-2 font-body text-[15px] text-[hsl(var(--md-ink))] outline-none focus:border-[hsl(var(--md-ink))]"
          />
        </div>
        <div>
          <label className="font-eyebrow text-[hsl(var(--md-stone))] block mb-1">Légende (optionnelle)</label>
          <input
            type="text"
            value={chap.caption || ""}
            onChange={(e) => onChange({ caption: e.target.value })}
            className="input-field"
          />
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-2">
            <label className="font-eyebrow text-[hsl(var(--md-stone))]">Images approuvées</label>
            <button type="button" onClick={addImage} className="font-eyebrow text-[hsl(var(--md-link))]">+ Ajouter</button>
          </div>
          <ul className="space-y-3">
            {(chap.images || []).map((im, i) => (
              <li key={i} className="border border-[hsl(var(--md-line))] p-3 grid md:grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-[12px] text-[hsl(var(--md-stone))] block mb-1">URL</label>
                  <input
                    type="text"
                    value={im.url || ""}
                    onChange={(e) => setImage(i, { url: e.target.value })}
                    className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-1 font-body text-[14px] text-[hsl(var(--md-ink))]"
                  />
                </div>
                <div>
                  <label className="font-body text-[12px] text-[hsl(var(--md-stone))] block mb-1">Légende</label>
                  <input
                    type="text"
                    value={im.caption || ""}
                    onChange={(e) => setImage(i, { caption: e.target.value })}
                    className="w-full bg-transparent border-b border-[hsl(var(--md-line-strong))] py-1 font-body text-[14px] text-[hsl(var(--md-ink))]"
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button type="button" onClick={() => removeImage(i)} className="font-eyebrow text-[hsl(var(--md-stone))]">Retirer</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

